import {
  ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CALLBACK_ID,
  buildAssistantWorkspaceGoogleRedirectUri,
} from "../../../config/assistantWorkspaceGoogleOauthPolicy.ts";

const AUTH_RETURN_CONTEXT_KEY =
  "ystnj.assistantSpreadsheetBridge.returnContext";
const AUTH_COMPLETION_CONTEXT_KEY =
  "ystnj.assistantSpreadsheetBridge.completionContext";
export const ASSISTANT_SPREADSHEET_BRIDGE_CALLBACK_ID =
  ASSISTANT_WORKSPACE_GOOGLE_OAUTH_CALLBACK_ID;
export const ASSISTANT_SPREADSHEET_BRIDGE_SETTINGS_ROUTE =
  "/school/settings/general";

export type AssistantSpreadsheetBridgeReturnContext = {
  originRoute: string;
  workbookArtifactPath?: string | null;
  workbookLabel?: string | null;
  workbookFileName?: string | null;
  spreadsheetDownloadPath?: string | null;
  startedAt: string;
};

const canUseStorage = (): boolean =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const readStoredJson = <T>(key: string): T | null => {
  if (!canUseStorage()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeStoredJson = (key: string, value: unknown): void => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(key, JSON.stringify(value));
};

const removeStoredJson = (key: string): void => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.removeItem(key);
};

export const storeAssistantSpreadsheetBridgeReturnContext = (
  context: AssistantSpreadsheetBridgeReturnContext,
): void => {
  writeStoredJson(AUTH_RETURN_CONTEXT_KEY, context);
};

export const getAssistantSpreadsheetBridgeReturnContext =
  (): AssistantSpreadsheetBridgeReturnContext | null =>
    readStoredJson<AssistantSpreadsheetBridgeReturnContext>(
      AUTH_RETURN_CONTEXT_KEY,
    );

export const clearAssistantSpreadsheetBridgeReturnContext = (): void => {
  removeStoredJson(AUTH_RETURN_CONTEXT_KEY);
};

export const storeAssistantSpreadsheetBridgeCompletionContext = (
  context: AssistantSpreadsheetBridgeReturnContext,
): void => {
  writeStoredJson(AUTH_COMPLETION_CONTEXT_KEY, context);
};

export const consumeAssistantSpreadsheetBridgeCompletionContext =
  (): AssistantSpreadsheetBridgeReturnContext | null => {
    const context = readStoredJson<AssistantSpreadsheetBridgeReturnContext>(
      AUTH_COMPLETION_CONTEXT_KEY,
    );
    if (context) {
      removeStoredJson(AUTH_COMPLETION_CONTEXT_KEY);
    }
    return context;
  };

export type AssistantSpreadsheetBridgeCallbackQuery = {
  isBridgeCallback: boolean;
  state: string | null;
  code: string | null;
  error: string | null;
  errorDescription: string | null;
  returnTo: string | null;
};

export const buildAssistantSpreadsheetBridgeCallbackUri = (input?: {
  returnTo?: string | null;
}): string => {
  if (typeof window === "undefined") {
    return "https://example.com/auth?bridgeCallback=assistant-spreadsheet";
  }

  return (
    buildAssistantWorkspaceGoogleRedirectUri(window.location.origin) ??
    "https://example.com/auth?bridgeCallback=assistant-spreadsheet"
  );
};

export const readAssistantSpreadsheetBridgeCallbackQuery = (
  search: string,
): AssistantSpreadsheetBridgeCallbackQuery => {
  const params = new URLSearchParams(search);
  return {
    isBridgeCallback:
      params.get("bridgeCallback") === ASSISTANT_SPREADSHEET_BRIDGE_CALLBACK_ID,
    state: params.get("state"),
    code: params.get("code"),
    error: params.get("error"),
    errorDescription: params.get("error_description"),
    returnTo: params.get("returnTo"),
  };
};
